
import { ChevronDown, ImagePlus } from "lucide-react";
import type { EventFragment } from "~/__generated__/graphql/graphql";
import { Avatar } from "~/components/Avatar";
import { Text } from "~/components/Text";
import { useEffect, useId, useRef, useState } from "react";
import { useCheckoutSession } from "./CheckoutSession";

import { TextField } from "~/components/Fields";

import { useUser } from "~/hooks/use-user";
import type { CheckoutProfileResult } from "~/routes/api.events.$eventId.checkout-profile";
import { CheckoutQuestionStep } from "./CheckoutQuestionStep";

import { ProfilePhotoCropper } from "./ProfilePhotoCropper";

type ProfileStage = "basic" | "photo";

type Props = {
  eventId: string;
  host?: EventFragment["hosts"][number];
  onComplete: () => void;
};

// Keep short bios consistent with mobile profile setup.
const PROFILE_SHORT_BIO_MAX_LENGTH = 120;

const supportedImageTypes = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
]);

function isRealName(name: string | null | undefined) {
  const trimmed = name?.trim() ?? "";
  return (
    trimmed.length > 0 &&
    trimmed.length <= 100 &&
    trimmed.toLowerCase() !== "anonymous user" &&
    /\p{L}/u.test(trimmed)
  );
}

export function ApprovalProfileStep({ eventId, host, onComplete }: Props) {
  const { user } = useUser();
  const checkout = useCheckoutSession();
  const photoInputRef = useRef<HTMLInputElement>(null);
  const genderInputId = useId();
  const headlineInputId = useId();
  const initialName = isRealName(user?.name) ? user?.name.trim() ?? "" : "";
  const [name, setName] = useState(checkout.draft.profile.name || initialName);
  const [age, setAge] = useState(checkout.draft.profile.age || (user?.age ? String(user.age) : ""));
  const [gender, setGender] = useState(checkout.draft.profile.gender || user?.gender?.trim().toLowerCase() || "");
  const [headline, setHeadline] = useState(checkout.draft.profile.headline || user?.headline?.trim() || "");
  const [profileImageUrl, setProfileImageUrl] = useState(
    checkout.draft.profile.profileImageUrl || user?.profileImageUrl?.trim() || "",
  );
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState<ProfileStage>(() => {
    const savedStage = checkout.draft.profile.stage;
    const validDetails = isRealName(name) && Number.isInteger(Number(age)) &&
      Number(age) >= 18 && Number(age) <= 100 && gender.trim() &&
      (headline.trim() || user?.bio?.trim());
    // Old per-field drafts reopen together; a valid photo-stage draft stays there.
    if (savedStage) return validDetails && ["photo", "complete"].includes(savedStage) ? "photo" : "basic";
    return user && initialName && user.age >= 18 && user.age <= 100 && user.gender.trim() &&
      (user.headline.trim() || user.bio.trim()) ? "photo" : "basic";
  });
  const updateDraft = checkout.update;
  useEffect(() => {
    updateDraft({ profile: { name, age, gender, headline, profileImageUrl, stage } });
  }, [name, age, gender, headline, profileImageUrl, stage, updateDraft]);

  const clearFieldError = (field: string) => {
    setFieldErrors(current => {
      const next = { ...current };
      delete next[field];
      return next;
    });
    setError(null);
  };

  const saveProfile = async (nextProfileImageUrl = profileImageUrl) => {
    setLoading(true);
    setError(null);
    setFieldErrors({});
    if (!await checkout.flush() || !checkout.canContinue()) { setLoading(false); return; }
    const formData = new FormData();
    formData.append("intent", "save-profile");
    formData.append("name", name.trim());
    formData.append("age", age);
    formData.append("gender", gender);
    formData.append("headline", headline.trim());
    formData.append("profileImageUrl", nextProfileImageUrl);

    try {
      const response = await fetch(`/api/events/${eventId}/checkout-profile`, {
        method: "POST",
        body: formData,
      });
      const result = (await response.json()) as CheckoutProfileResult;
      if (!result.ok) {
        const errors = result.fieldErrors ?? {};
        setFieldErrors(errors);
        if (errors.name || errors.age || errors.gender || errors.headline) setStage("basic");
        else if (errors.profileImageUrl) setStage("photo");
        throw new Error(result.error ?? "Check the highlighted fields.");
      }
      if (checkout.canContinue()) onComplete();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Profile update failed.",
      );
    } finally {
      setLoading(false);
    }
  };

  const advance = async () => {
    setError(null);
    const errors: Record<string, string> = {};
    if (!isRealName(name)) errors.name = "Enter your name.";
    const ageValue = Number(age);
    if (!Number.isInteger(ageValue) || ageValue < 18 || ageValue > 100) {
      errors.age = age.trim() ? "Enter a valid age (18+)." : "Enter your age.";
    }
    if (!gender.trim()) errors.gender = "Choose an option.";
    if (!headline.trim() && !user?.bio?.trim()) {
      errors.headline = "Add a short bio.";
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;
    setName(name.trim());
    if (!await checkout.flush() || !checkout.canContinue()) return;
    if (profileImageUrl) await saveProfile();
    else setStage("photo");
  };

  const uploadPhoto = async (file: File) => {
    setLoading(true);
    setError(null);
    if (!await checkout.flush() || !checkout.canContinue()) { setLoading(false); return; }
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await fetch("/api/media/upload", {
        method: "POST",
        body: formData,
      });
      const result = (await response.json()) as {
        imageUrl?: string;
        error?: string;
      };
      if (!response.ok || !result.imageUrl) {
        throw new Error(result.error ?? "Upload failed.");
      }
      setProfileImageUrl(result.imageUrl);
      setSelectedPhoto(null);
      await saveProfile(result.imageUrl);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Upload failed.");
      setLoading(false);
    }
  };

  const choosePhoto = (file: File | undefined) => {
    if (!file) return;
    setError(null);
    if (!supportedImageTypes.has(file.type)) {
      setError("Choose a JPEG, PNG, or WebP image.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("Choose an image smaller than 10 MB.");
      return;
    }
    setProfileImageUrl("");
    setSelectedPhoto(file);
  };

  const focusNextFieldOnEnter = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing || event.keyCode === 229) return;
    const fields = Array.from(event.currentTarget.form?.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("input, textarea, select") ?? []);
    const index = fields.findIndex(field => field === event.currentTarget);
    if (index < 0 || index === fields.length - 1) return;
    event.preventDefault();
    fields[index + 1].focus();
  };

  if (stage === "basic") {
    return (
      <CheckoutQuestionStep
        title="About you"

        onSubmit={advance}
        onBack={() => checkout.setStep("tickets")}
        submitLabel={profileImageUrl ? "Save profile" : "Next"}
        loading={loading}
        error={error}
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2.5">
            {host && <Avatar user={host.user} size={38} className="shrink-0" />}
            <Text as="p" variant="secondary" size="sm" className="leading-relaxed">
              <span className="font-bold text-butter-green">{host?.user.firstName?.trim() || "The host"}</span>{" "}
              is putting the guest list together. Tell them a little about yourself.
            </Text>
          </div>
          <div className="grid grid-cols-[minmax(0,1fr)_6rem] gap-3">
            <TextField
              label="Your name"
              autoComplete="name"
              enterKeyHint="next"
              placeholder="First and last name"
              maxLength={100}
              value={name}
              disabled={loading}
              onKeyDown={focusNextFieldOnEnter}
              onChange={event => { setName(event.target.value); clearFieldError("name"); }}
              error={fieldErrors.name}
            />
            <TextField
              label="Age"
              inputMode="numeric"
              enterKeyHint="next"
              placeholder="24"
              maxLength={3}
              value={age}
              disabled={loading}
              onKeyDown={focusNextFieldOnEnter}
              onChange={event => { setAge(event.target.value); clearFieldError("age"); }}
              error={fieldErrors.age}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor={headlineInputId} className="font-sans text-sm font-bold text-butter-green">A line about you</label>
            <textarea
              id={headlineInputId}
              rows={2}
              enterKeyHint="next"
              maxLength={PROFILE_SHORT_BIO_MAX_LENGTH}
              value={headline}
              disabled={loading}
              onKeyDown={focusNextFieldOnEnter}
              placeholder={user?.bio?.trim() || "Run club dropout, matcha loyalist, will go to anything with a DJ"}
              aria-invalid={fieldErrors.headline ? true : undefined}
              aria-describedby={`${headlineInputId}-hint${fieldErrors.headline ? ` ${headlineInputId}-error` : ""}`}
              onChange={event => { setHeadline(event.target.value); clearFieldError("headline"); }}
              className={`block w-full resize-none rounded-[9px] border bg-white px-3 py-3 font-sans text-base text-butter-green outline-none placeholder:text-placeholder-text ${fieldErrors.headline ? "border-butter-pink" : "border-butter-cream focus:border-butter-green"}`}
            />
            <div id={`${headlineInputId}-hint`} className="flex flex-wrap justify-between gap-x-3 font-sans text-sm text-butter-green">
              <span>Shown on your profile.</span>
              <span className="tabular-nums">{headline.length} / {PROFILE_SHORT_BIO_MAX_LENGTH}<span className="sr-only"> characters</span></span>
            </div>
            {fieldErrors.headline && <p id={`${headlineInputId}-error`} role="alert" className="font-sans text-sm text-butter-pink">{fieldErrors.headline}</p>}
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor={genderInputId} className="font-sans text-sm font-bold text-butter-green">Gender association</label>
            <div className="relative">
              <select
                id={genderInputId}
                value={gender}
                disabled={loading}
                aria-invalid={fieldErrors.gender ? true : undefined}
                aria-describedby={fieldErrors.gender ? `${genderInputId}-error` : undefined}
                onChange={event => { setGender(event.target.value); clearFieldError("gender"); }}
                className={`block w-full appearance-none rounded-[9px] border bg-white py-3 pl-3 pr-10 font-sans text-base outline-none [&>option:not(:disabled)]:text-butter-green ${gender ? "text-butter-green" : "text-placeholder-text"} ${fieldErrors.gender ? "border-butter-pink" : "border-butter-cream focus:border-butter-green"}`}
              >
                <option value="" disabled>Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="lgbtqia">LGBTQIA+</option>
                {gender && !["female", "male", "lgbtqia"].includes(gender) && <option value={gender}>{gender}</option>}
              </select>
              <ChevronDown
                className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-butter-green"
                aria-hidden
              />
            </div>
            {fieldErrors.gender && <p id={`${genderInputId}-error`} role="alert" className="font-sans text-sm text-butter-pink">{fieldErrors.gender}</p>}
          </div>
        </div>
      </CheckoutQuestionStep>
    );
  }

  return (
    <>
      <input
        ref={photoInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        disabled={loading}
        onChange={event => {
          choosePhoto(event.target.files?.[0]);
          event.target.value = "";
        }}
      />
      {selectedPhoto || profileImageUrl ? (
        <ProfilePhotoCropper file={selectedPhoto ?? profileImageUrl} onSaveExisting={!selectedPhoto ? () => saveProfile() : undefined} loading={loading} uploadError={error} onBack={() => setStage("basic")} onReplace={() => photoInputRef.current?.click()} onConfirm={uploadPhoto} />
      ) : (
        <CheckoutQuestionStep
          title="Add a photo"
          onBack={() => setStage("basic")}
          description="One where it’s clearly you. Group pics don’t count."
          onSubmit={() => {}}
          submitLabel="Next"
          submitDisabled
          loading={loading}
          error={error}
        >
          <div className="sm:min-h-[440px]">
          <button
            type="button"
            onClick={() => photoInputRef.current?.click()}
            disabled={loading}
            aria-label="Choose a photo"
            className="mx-auto flex aspect-square w-3/5 cursor-pointer flex-col items-center justify-center gap-4 overflow-hidden rounded-3xl border-2 border-dashed border-butter-cream bg-white/70 text-butter-green transition-colors hover:bg-white disabled:cursor-default"
          >
            <ImagePlus className="size-12" strokeWidth={1.7} aria-hidden />
            <span className="font-sans text-base font-bold">Choose a photo</span>
          </button>
          </div>
        </CheckoutQuestionStep>
      )}
    </>
  );
}
